import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  Send,
  X,
  Pencil,
  Printer,
  Wrench,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { loadIssuedPaperworkPreview, releasePreview } from '../../utils/documentPreview';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import PaperworkAmendModal from '../../components/PaperworkAmendModal';
import DocumentMailStatus, { mailNeedsSend } from '../../components/DocumentMailStatus';
import BuildingPaperworkTabs, {
  normalizePaperworkTab,
} from '../../components/BuildingPaperworkTabs';
import BuildingPaperworkCouncilTab from '../../components/BuildingPaperworkCouncilTab';
import { compareSsCode } from '../../utils/ssCodeSort';
import { formatDueDateLocal } from '../../utils/dueDate';
import { JOB_STATUS_LABELS } from '../../utils/jobStatusColor';
import { buildPaperworkYears } from '../../utils/paperworkYearCards';
import { inspectionResultTextClass } from '../../utils/inspectionResultColor';
import {
  paperworkFlowCardClass,
  paperworkDueState,
  paperworkWorkState,
  paperworkWorkStateLabel,
  paperworkDueChipClass,
  paperworkWorkChipClass,
} from '../../utils/paperworkFlowStatus';
import {
  captureWindowScroll,
  pinWindowScroll,
  preventFocusScroll,
} from '../../utils/windowScrollGuard';

function formatDate(v) {
  if (!v) return '—';
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

function typeBadge(type) {
  if (type === 'FORM_12A') {
    return 'bg-green-100 text-green-900 border border-green-300';
  }
  if (type === 'S_RAD') {
    return 'bg-blue-100 text-blue-900 border border-blue-300';
  }
  if (type === 'DEFECT_NOTICE') {
    return 'bg-red-100 text-red-900 border border-red-300';
  }
  return 'bg-gray-100 text-gray-600 border border-gray-200';
}

function rowToneClass(type) {
  if (type === 'FORM_12A') return 'bg-green-50/80';
  if (type === 'S_RAD') return 'bg-blue-50/80';
  if (type === 'DEFECT_NOTICE') return 'bg-red-50/80';
  return 'bg-white';
}

function passFailBadge(v) {
  if (!v) return 'text-gray-400';
  return inspectionResultTextClass(v);
}

function escapePrint(value) {
  return String(value || '—')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Paper form. The inspector has done the visit; the admin fills or files this copy. */
function printManualSheet(row, building) {
  const w = window.open('', '_blank');
  if (!w) {
    toast.error('Popup blocked — allow popups to print');
    return;
  }
  const ssName =
    row?.ss_name && row.ss_name !== row.ss_code ? ` — ${escapePrint(row.ss_name)}` : '';
  w.document.write(`<!DOCTYPE html><html><head><title>Manual print ${escapePrint(row?.ss_code)}</title>
    <style>
      body { font-family: sans-serif; padding: 28px; color: #111; }
      h1 { font-size: 22px; margin: 0 0 8px; }
      p { margin: 6px 0; }
      .line { border-bottom: 1px solid #111; height: 32px; margin: 6px 0 18px; }
    </style></head><body>
    <h1>Manual print</h1>
    <p>The inspector completed the visit. The admin completes this paper form.</p>
    <p><strong>Building</strong> ${escapePrint(building?.name)}</p>
    <p><strong>SS</strong> ${escapePrint(row?.ss_code)}${ssName}</p>
    <p><strong>Inspector</strong> ${escapePrint(row?.iqp_name || row?.default_worker_name)}</p>
    <p><strong>Issued form</strong> (Form 12A / S-RaD / Defect)</p><div class="line"></div>
    <p><strong>Inspection report</strong></p><div class="line"></div>
    <p><strong>Notes</strong></p><div class="line"></div><div class="line"></div>
    </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

function classifyLocal(name) {
  const t = String(name || '').toLowerCase();
  if (/defect/.test(t)) return 'DEFECT_NOTICE';
  if (/s[\s\-]?rad|srad/.test(t)) return 'S_RAD';
  if (/12\s*a|form\s*12a|compliance/.test(t)) return 'FORM_12A';
  return '';
}

function isIssuedRow(row) {
  return !!(row?.paperwork_type || row?.document_id);
}

function issuedFormLabel(type) {
  if (type === 'FORM_12A') return 'Form 12A';
  if (type === 'S_RAD') return 'S-RaD';
  if (type === 'DEFECT_NOTICE') return 'Defect report';
  return 'Issued form';
}

function certificateTitle(row) {
  if (row?.paperwork_label) return row.paperwork_label;
  if (row?.paperwork_type) return issuedFormLabel(row.paperwork_type);
  return '';
}

function cardStatusLabel(row) {
  const work = paperworkWorkState(row);
  if (work) return paperworkWorkStateLabel(work);
  const issued = isIssuedRow(row);
  if (issued && (row.paperwork_type === 'FORM_12A' || row.paperwork_type === 'S_RAD')) return 'Completed';
  if (issued && row.paperwork_type === 'DEFECT_NOTICE') return 'Failed';
  const st = String(row?.job_status || '').toUpperCase();
  if (!st || st === 'NO JOB') return '';
  return JOB_STATUS_LABELS[st] || st;
}

function inspectionDueText(row) {
  const due = paperworkDueState(row);
  if (!due.label) return '';
  return due.term ? `${due.label} (${due.term})` : due.label;
}

function innerSsCardClass(row, filter) {
  if (filter === 'INSPECTION_REPORT' && row.inspection_report_record_id) {
    // Keep filter highlight, but prefer flow colour when a form already exists
    if (row.paperwork_type || row.service_report_record_id) {
      return paperworkFlowCardClass(row);
    }
    return 'bg-purple-100 border-purple-400';
  }
  return paperworkFlowCardClass(row);
}

export default function BuildingPaperwork() {
  const { id: buildingId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const docsBase = `/buildings/${buildingId}/documents`;
  const activeTab = normalizePaperworkTab(searchParams.get('tab'));

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'forms') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const [paperFilter, setPaperFilter] = useState('all');
  const [docPick, setDocPick] = useState(null);
  const [docChoice, setDocChoice] = useState('');
  const [manualFile, setManualFile] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [viewerStep, setViewerStep] = useState('preview'); // preview | actions
  const [viewerChain, setViewerChain] = useState([]);
  const [viewerTrail, setViewerTrail] = useState([]); // previously viewed ids for quick return
  const [sendTarget, setSendTarget] = useState(null);
  const [sendTo, setSendTo] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [amendRow, setAmendRow] = useState(null);
  const [extOpen, setExtOpen] = useState(false);
  const [extSsCode, setExtSsCode] = useState('');
  const [extName, setExtName] = useState('');
  const [extType, setExtType] = useState('');
  const [extDocId, setExtDocId] = useState('');
  const [extWorkerId, setExtWorkerId] = useState('');
  const [extBusy, setExtBusy] = useState(false);
  const [mailLog, setMailLog] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['building', buildingId, 'paperwork'],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/paperwork`)).data?.data,
    enabled: !!buildingId,
    placeholderData: (prev) => prev,
  });

  const building = data?.building;
  const summary = data?.summary;
  const allRows = useMemo(() => {
    const list = Array.isArray(data?.rows) ? data.rows : [];
    return [...list].sort((a, b) => compareSsCode(a?.ss_code, b?.ss_code));
  }, [data?.rows]);

  const issuedRows = useMemo(() => allRows.filter(isIssuedRow), [allRows]);
  const pendingRows = useMemo(() => allRows.filter((r) => !isIssuedRow(r)), [allRows]);
  const yearCards = useMemo(
    () =>
      buildPaperworkYears({
        cycleStart: summary?.cycle_start,
        totalYears: summary?.total_years,
        ssRows: allRows,
        history: data?.history,
      }),
    [summary?.cycle_start, summary?.total_years, allRows, data?.history]
  );
  const matchesFilter = (row) => {
    if (paperFilter === 'PENDING') return !isIssuedRow(row);
    if (paperFilter === 'FORM_12A' || paperFilter === 'S_RAD' || paperFilter === 'DEFECT_NOTICE') {
      return row.paperwork_type === paperFilter;
    }
    if (paperFilter === 'INSPECTION_REPORT') return !!row.inspection_report_record_id;
    return true;
  };
  const pendingCount = summary?.pending_count ?? pendingRows.length;

  const { data: workersList = [] } = useQuery({
    queryKey: ['workers', 'for-paperwork-amend'],
    queryFn: async () => {
      const response = await api.get('/workers', { params: { limit: 500, status: 'active' } });
      return response.data?.data || response.data?.workers || [];
    },
  });

  const confirmDocPick = async () => {
    const row = docPick;
    const choice = docChoice;
    if (!row || !choice) return;
    if (choice === 'report' && !row.inspection_report_record_id) {
      toast.error('Inspection report is not issued for this SS');
      return;
    }
    if (choice === 'manual') {
      try {
        if (manualFile) {
          const fd = new FormData();
          fd.append('file', manualFile);
          await api.post(
            `/buildings/${buildingId}/paperwork/${encodeURIComponent(row.ss_code)}/manual-print`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' }, skipErrorToast: true }
          );
          toast.success('Manual print copy saved');
          queryClient.invalidateQueries(['building', buildingId, 'paperwork']);
        }
        printManualSheet(row, building);
      } catch (e) {
        toast.error(e.response?.data?.error || e.message || 'Could not save the manual print');
        return;
      }
      setDocPick(null);
      setDocChoice('');
      setManualFile(null);
      return;
    }
    setDocPick(null);
    setDocChoice('');
    setManualFile(null);
    if (choice === 'report') {
      await openPreviewByRecordId(row.inspection_report_record_id, { pushTrail: false });
      return;
    }
    if (choice === 'service') {
      await openPreviewByRecordId(row.service_report_record_id, { pushTrail: false });
      return;
    }
    await openPreview(row);
  };

  const closeViewer = () => {
    releasePreview(viewer);
    setViewer(null);
    setViewerStep('preview');
    setViewerChain([]);
    setViewerTrail([]);
  };

  const fetchIssuedDetail = async (recordId) => {
    const res = await api.get(`/paperwork/issued-forms/${recordId}`);
    return res.data?.data || res.data;
  };

  const openPreviewByRecordId = async (recordId, { pushTrail = true } = {}) => {
    if (!recordId) return;
    const keepY = captureWindowScroll();
    pinWindowScroll(keepY, 1200);
    try {
      if (pushTrail && (viewer?.row?.record_id || viewer?.row?.id)) {
        const prevId = viewer.row.record_id || viewer.row.id;
        const prevLabel =
          viewer.row.paperwork_label || viewer.row.document_name || viewer.row.paperwork_type;
        setViewerTrail((t) => {
          const next = [
            { id: prevId, label: prevLabel, paperwork_type: viewer.row.paperwork_type },
            ...t.filter((x) => String(x.id) !== String(prevId)),
          ];
          return next.slice(0, 6);
        });
      }
      const [preview, detail] = await Promise.all([
        loadIssuedPaperworkPreview(api, { recordId }),
        fetchIssuedDetail(recordId),
      ]);
      const item = detail?.item || {};
      setViewerStep('preview');
      setViewer({
        row: {
          ...item,
          record_id: item.id || recordId,
          id: item.id || recordId,
          paperwork_label:
            item.display_name ||
            item.document_name ||
            item.form_short ||
            item.paperwork_label ||
            item.paperwork_type,
          issued_date: item.issued_date || item.issued_date_display,
          iqp_name: item.worker_name || item.iqp_name,
          document_id: detail?.document_id || item.document_id,
          mail_status: item.mail_status || '',
          mail_to: item.mail_to || '',
        },
        ...preview,
      });
      setViewerChain(Array.isArray(detail?.linked?.chain) ? detail.linked.chain : []);
      pinWindowScroll(keepY, 1200);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Cannot open document');
      pinWindowScroll(keepY, 600);
    }
  };

  const openPreview = async (row) => {
    if (!row?.ss_code && !row?.document_id && !row?.record_id) {
      toast.error('No issued document for this SS yet');
      return;
    }
    if (row.record_id) {
      await openPreviewByRecordId(row.record_id, { pushTrail: false });
      return;
    }
    const keepY = captureWindowScroll();
    pinWindowScroll(keepY, 1200);
    try {
      const preview = await loadIssuedPaperworkPreview(api, {
        buildingId,
        ssCode: row.ss_code,
        documentId: row.document_id,
      });
      setViewerStep('preview');
      setViewer({ row, ...preview });
      setViewerChain([]);
      setViewerTrail([]);
      pinWindowScroll(keepY, 1200);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Cannot open document');
      pinWindowScroll(keepY, 600);
    }
  };

  const printViewer = () => {
    if (!viewer) return;
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Popup blocked — allow popups to print');
      return;
    }
    if (viewer.kind === 'html' && viewer.html) {
      w.document.write(viewer.html);
      w.document.close();
      w.focus();
      w.print();
      return;
    }
    if (viewer.blobUrl) {
      w.location.href = viewer.blobUrl;
      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch (_) {
          /* user can print from new tab */
        }
      }, 400);
      return;
    }
    toast.error('Print not available for this file');
  };

  const openSend = (row, opts = {}) => {
    const keepY = captureWindowScroll();
    const documentId = opts.documentId || row?.document_id;
    const docLabel =
      opts.label || row?.paperwork_label || row?.paperwork_type || 'Paperwork';
    if (!documentId) {
      toast.error('No issued document to send');
      return;
    }
    setSendTarget({ ...row, document_id: documentId, paperwork_label: docLabel });
    setSendTo(
      opts.to ||
        row?.mail_to ||
        row?.inspection_report_mail_to ||
        building?.owner_email ||
        ''
    );
    setSendSubject(
      opts.subject ||
        `[Paperwork] ${building?.name || 'Building'} — ${docLabel}`
    );
    setSendMessage(
      opts.message ||
        [
          `Building: ${building?.name || ''}`,
          building?.address ? `Address: ${building.address}` : '',
          `Specified system: ${row.ss_code}${row.ss_name && row.ss_name !== row.ss_code ? ` (${row.ss_name})` : ''}`,
          `Document: ${docLabel}`,
          `IQP: ${row.iqp_name || '—'}`,
          '',
          'Please find the attached inspection paperwork.',
        ]
          .filter(Boolean)
          .join('\n')
    );
    pinWindowScroll(keepY, 1200);
  };

  const onMailIconClick = (e, row, { status, documentId, label }) => {
    e.stopPropagation();
    if (!documentId) {
      toast.error('No document file to mail yet');
      return;
    }
    if (mailNeedsSend(status)) {
      openSend(row, { documentId, label });
      return;
    }
    openMailLog(documentId, label, row);
  };

  const openMailLog = async (docId, label, rowForResend = null) => {
    if (!docId) return;
    try {
      const data = (await api.get(`${docsBase}/${docId}/mail-log`)).data?.data;
      setMailLog({
        ...(data || {}),
        documentId: docId,
        label: label || data?.name || 'Mail log',
        rowForResend: rowForResend || null,
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Mail log unavailable');
    }
  };

  const sendMutation = useMutation({
    mutationFn: async ({ docId, to, subject, message }) =>
      (await api.post(`${docsBase}/${docId}/send`, { to, subject, message })).data,
    onSuccess: (data) => {
      toast.success(data?.result?.stubbed ? 'Send queued (SMTP stub — check server log)' : 'Sent');
      setSendTarget(null);
      queryClient.invalidateQueries(['building', buildingId, 'paperwork']);
      queryClient.invalidateQueries(['docs-browse', `building-${buildingId}`]);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Send failed'),
  });

  const openExternalRegister = () => {
    const keepY = captureWindowScroll();
    const first = pendingRows[0];
    setExtSsCode(first?.ss_code || '');
    setExtName('');
    setExtType('');
    setExtDocId('');
    setExtWorkerId(first?.default_worker_id ? String(first.default_worker_id) : '');
    setExtOpen(true);
    pinWindowScroll(keepY, 1200);
  };

  const onExtNameChange = (v) => {
    setExtName(v);
    const guessed = classifyLocal(v);
    if (guessed) setExtType(guessed);
  };

  const submitExternalRegister = async () => {
    if (!extSsCode) {
      toast.error('Choose an SS that is not issued yet');
      return;
    }
    if (!extType) {
      toast.error('Choose Form 12A, S-RaD, or Defect (or enter a filename that classifies)');
      return;
    }
    const pending = pendingRows.find((r) => r.ss_code === extSsCode);
    setExtBusy(true);
    try {
      await api.post(`/buildings/${buildingId}/paperwork/register-external`, {
        ssCode: extSsCode,
        paperworkType: extType,
        documentId: extDocId.trim() ? Number(extDocId.trim()) : undefined,
        label: extName.trim() || undefined,
        notes: 'External / Notified registration',
        jobId: pending?.job_id || undefined,
        workerId: extWorkerId.trim() || undefined,
      });
      toast.success(`Registered ${extType.replace('_', ' ')} for ${extSsCode}`);
      setExtOpen(false);
      queryClient.invalidateQueries(['building', buildingId, 'paperwork']);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Register failed');
    } finally {
      setExtBusy(false);
    }
  };

  const inspectionsPath = buildingId
    ? `/jobs/building/${encodeURIComponent(buildingId)}`
    : '/inspections';
  const unitsPath = buildingId ? `/buildings/${encodeURIComponent(buildingId)}/units` : '/';

  const reinspectionPath = (row) => {
    if (row?.job_id) return `/jobs/${row.job_id}?tab=action`;
    const ss = row?.ss_code ? `?ss=${encodeURIComponent(row.ss_code)}` : '';
    return `${inspectionsPath}${ss}`;
  };

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={building?.name}
        title="Paperwork"
        infoTitle="SS paperwork"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p>
              Paperwork is post-inspection document work for this building. Tabs:{' '}
              <strong>SS forms</strong> (issue matrix) and <strong>Council / Agency</strong> (pack,
              send, and upload replies). The inspector does the visit. Admin paperwork can use the
              issued form, the inspection report, or a manual print, with an optional upload of
              that paper copy.
            </p>
            <p>
              Portfolio search across all buildings stays under menu{' '}
              <strong>Issued document</strong>. Dashboard paperwork queues deep-link into this page.
            </p>
          </div>
        }
      />

      <BuildingPaperworkTabs active={activeTab} onChange={setTab} />

      {activeTab === 'council' && (
        <BuildingPaperworkCouncilTab
          buildingId={buildingId}
          building={building}
          summary={summary}
        />
      )}

      {activeTab === 'forms' && (
        <>
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            {
              id: 'all',
              label: 'total year',
              value: summary.total_years ?? 0,
              tone: 'text-blue-700',
              ring: 'ring-blue-600',
            },
            { id: 'FORM_12A', label: 'Form 12A', value: summary.form12a_count, tone: 'text-green-700', ring: 'ring-green-600' },
            { id: 'S_RAD', label: 'S-RaD', value: summary.srad_count, tone: 'text-yellow-600', ring: 'ring-yellow-500' },
            { id: 'DEFECT_NOTICE', label: 'Defect report', value: summary.defect_count, tone: 'text-red-600', ring: 'ring-red-600' },
            {
              id: 'INSPECTION_REPORT',
              label: 'inspection report',
              value:
                summary.inspection_report_count ??
                issuedRows.filter((r) => r.inspection_report_record_id).length,
              tone: 'text-purple-700',
              ring: 'ring-purple-600',
            },
            { id: 'PENDING', label: 'Pending', value: summary.pending_count, tone: 'text-slate-700', ring: 'ring-slate-500' },
          ].map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setPaperFilter(c.id)}
              aria-pressed={paperFilter === c.id}
              className={`rounded-lg border bg-white px-3 py-2.5 text-left ${
                paperFilter === c.id ? `ring-2 ${c.ring}` : ''
              }`}
            >
              <p className={`text-xs font-semibold ${c.tone}`}>{c.label}</p>
              <p className={`text-xl font-bold tabular-nums ${c.tone}`}>{c.value}</p>
            </button>
          ))}
        </div>
      )}

      {pendingCount > 0 && paperFilter === 'all' && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 space-y-2">
          <p className="font-medium">
            {pendingCount} SS have no form yet. They still appear on each year card. Continue inspection
            or SS Units, then issue from the app (or Register external for partner scans).
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to={inspectionsPath}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-semibold"
            >
              Continue inspection / job
            </Link>
            <Link
              to={unitsPath}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-xs font-semibold text-amber-950"
            >
              Open SS Units
            </Link>
            {pendingRows.length > 0 && (
              <button
                type="button"
                onMouseDown={preventFocusScroll}
                onClick={openExternalRegister}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-300 bg-violet-50 text-xs font-semibold text-violet-900"
              >
                Register external
              </button>
            )}
          </div>
        </div>
      )}

      {isLoading && !data && <p className="px-4 py-8 text-gray-500 text-sm">Loading paperwork…</p>}
      {isError && (
        <p className="px-4 py-8 text-red-600 text-sm">
          {error?.response?.data?.error || 'Failed to load paperwork'}
        </p>
      )}
      {!(!data && isLoading) && !isError && (
        <div className="space-y-4">
          {yearCards.map((period, periodIndex) => {
            const visible = period.ss.filter(matchesFilter);
            return (
              <section
                key={period.due || 'open'}
                className={`rounded-xl border shadow-sm ${
                  period.hasDefect ? 'border-red-400 bg-red-100' : 'border-green-400 bg-green-100'
                }`}
              >
                <div className="px-4 pt-4">
                  <p className="text-2xl font-bold tracking-tight text-gray-900">
                    Due date {period.due ? formatDueDateLocal(period.due) : '—'}
                  </p>
                  {!summary?.cycle_start && (
                    <p className="mt-1 text-sm text-gray-700">
                      Set Contract / start date on Edit Building. Paperwork years start from that date.
                    </p>
                  )}
                </div>
                <div className="space-y-2 px-4 py-4">
                  {visible.map((row) => (
                    <div
                      key={row.ss_code}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setDocChoice('');
                        setManualFile(null);
                        setDocPick(row);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDocChoice('');
                          setManualFile(null);
                          setDocPick(row);
                        }
                      }}
                      className={`w-full rounded-lg border px-3 py-2.5 text-left cursor-pointer ${innerSsCardClass(
                        row,
                        paperFilter
                      )}`}
                    >
                      {(() => {
                        const issued = isIssuedRow(row);
                        const showPeople = issued || periodIndex === 0;
                        const dueText = inspectionDueText(row);
                        const dueMeta = paperworkDueState(row);
                        const workState = paperworkWorkState(row);
                        const workerName = row.iqp_name || row.default_worker_name || '';
                        const workerId = row.iqp_id || row.default_worker_id || '';
                        const status = showPeople ? cardStatusLabel(row) : '';
                        const cert = certificateTitle(row);
                        const showFormMail =
                          !!row.document_id &&
                          (row.paperwork_type === 'DEFECT_NOTICE' ||
                            row.paperwork_type === 'FORM_12A' ||
                            row.paperwork_type === 'S_RAD' ||
                            row.paperwork_type === 'SERVICE_REPORT');
                        const formMail = showFormMail ? row.mail_status || '' : '';
                        const formLabel = cert || issuedFormLabel(row.paperwork_type) || 'Form';
                        const irDocId = row.inspection_report_document_id;
                        const irMail = irDocId ? row.inspection_report_mail_status || '' : '';
                        return (
                          <>
                            {dueText ? (
                              <p className="text-sm font-semibold text-gray-900">
                                Anniversary {dueText}
                              </p>
                            ) : null}
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {dueMeta.state ? (
                                <span
                                  className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${paperworkDueChipClass(
                                    dueMeta.state
                                  )}`}
                                >
                                  {dueMeta.state === 'overdue' ? 'Overdue' : 'Due'}
                                </span>
                              ) : null}
                              {workState ? (
                                <span
                                  className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${paperworkWorkChipClass(
                                    workState
                                  )}`}
                                >
                                  {paperworkWorkStateLabel(workState)}
                                </span>
                              ) : null}
                              {row.is_external ? (
                                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-900 border border-violet-300">
                                  External
                                </span>
                              ) : null}
                            </div>
                            <p className="font-semibold text-gray-900">
                              {row.ss_code}
                              {row.ss_name && row.ss_name !== row.ss_code ? ` — ${row.ss_name}` : ''}
                            </p>
                            {showPeople ? (
                              <p className="mt-1 text-sm text-gray-800">
                                {workerName || 'Not assigned'}
                                {workerId && workerName && String(workerId) !== String(workerName) ? (
                                  <span className="block text-xs text-gray-600">{workerId}</span>
                                ) : null}
                              </p>
                            ) : null}
                            {status ? <p className="mt-1 text-sm font-medium text-gray-900">{status}</p> : null}
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-800">
                              <span>
                                {cert ||
                                  (paperFilter === 'INSPECTION_REPORT'
                                    ? 'Inspection report'
                                    : 'No form yet')}
                              </span>
                              {showFormMail ? (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"
                                  title={
                                    mailNeedsSend(formMail)
                                      ? 'Not sent / failed — click to send'
                                      : row.mail_to || 'Mail log'
                                  }
                                  onClick={(e) =>
                                    onMailIconClick(e, row, {
                                      status: formMail,
                                      documentId: row.document_id,
                                      label: formLabel,
                                    })
                                  }
                                >
                                  <DocumentMailStatus status={formMail} showLabel />
                                  {row.mail_to ? (
                                    <span className="truncate max-w-[10rem]">{row.mail_to}</span>
                                  ) : null}
                                </button>
                              ) : null}
                              {irDocId ? (
                                <>
                                  <span>· Inspection report</span>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"
                                    title={
                                      mailNeedsSend(irMail)
                                        ? 'Not sent / failed — click to send'
                                        : row.inspection_report_mail_to || 'Mail log'
                                    }
                                    onClick={(e) =>
                                      onMailIconClick(e, row, {
                                        status: irMail,
                                        documentId: irDocId,
                                        label: 'Inspection report',
                                      })
                                    }
                                  >
                                    <DocumentMailStatus status={irMail} showLabel />
                                    {row.inspection_report_mail_to ? (
                                      <span className="truncate max-w-[10rem]">
                                        {row.inspection_report_mail_to}
                                      </span>
                                    ) : null}
                                  </button>
                                </>
                              ) : null}
                            </div>
                            {issued ? (
                              <p className="mt-1 text-xs text-gray-600">
                                Certificate issued — click name to view
                              </p>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  ))}
                  {!visible.length && (
                    <p className="text-sm text-gray-600">No documents for this card.</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
        </>
      )}

      {docPick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="rounded-lg shadow-xl w-full max-w-md bg-white">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-900">Choose a document</h3>
              <p className="text-xs text-gray-500 mt-1">
                {docPick.ss_code} — the inspector did the visit. Choose the issued form, the
                inspection report, or a manual print.
              </p>
            </div>
            <div className="px-4 py-4 space-y-2">
              {[
                {
                  id: 'form',
                  label: issuedFormLabel(docPick.paperwork_type),
                  hint: null,
                  ready: !!(docPick.paperwork_type || docPick.document_id || docPick.record_id),
                },
                {
                  id: 'report',
                  label: 'Inspection report',
                  hint: null,
                  ready: !!docPick.inspection_report_record_id,
                },
                {
                  id: 'service',
                  label:
                    docPick.service_report_type === 'REINSPECTION_REPORT'
                      ? 'Re-inspection report'
                      : 'Service report',
                  hint: null,
                  ready: !!docPick.service_report_record_id,
                },
                {
                  id: 'manual',
                  label: 'Manual print',
                  hint: 'Inspector does the visit. Admin prints the paper form.',
                  ready: !!docPick.manual_print_document_id,
                  readyText: docPick.manual_print_name || 'Printed copy on file',
                  emptyText: 'No printed copy uploaded',
                },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDocChoice(opt.id)}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm ${
                    docChoice === opt.id
                      ? 'border-primary-600 bg-primary-50 font-semibold text-gray-900'
                      : 'border-slate-200 bg-white text-gray-800'
                  }`}
                >
                  <span>{opt.label}</span>
                  {opt.hint ? (
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">{opt.hint}</span>
                  ) : null}
                  {!opt.ready && (
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      {opt.emptyText || 'Not issued yet'}
                    </span>
                  )}
                  {opt.ready && opt.readyText ? (
                    <span className="block text-xs font-normal text-gray-600 mt-0.5">{opt.readyText}</span>
                  ) : null}
                </button>
              ))}
              {docChoice === 'manual' ? (
                <label className="block text-sm text-gray-800">
                  Upload printed copy (optional)
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.html,.htm"
                    className="mt-1 block w-full text-sm"
                    onChange={(e) => setManualFile(e.target.files?.[0] || null)}
                  />
                </label>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t">
              <button
                type="button"
                onClick={() => {
                  setDocPick(null);
                  setDocChoice('');
                  setManualFile(null);
                }}
                className="px-3 py-1.5 rounded-lg border text-sm font-semibold text-gray-800"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!docChoice}
                onClick={confirmDocPick}
                className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-40"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className={`rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col ${rowToneClass(
              viewer.row.paperwork_type
            )} bg-white`}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">
                  {viewer.row.paperwork_label || viewer.row.document_name}
                </h3>
                <p className="text-xs text-gray-500">
                  {viewer.row.ss_code} · {formatDate(viewer.row.issued_date)} · IQP:{' '}
                  {viewer.row.iqp_name || '—'}
                </p>
              </div>
              <button type="button" onClick={closeViewer} className="p-1 text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            {(viewerChain.length > 0 || viewerTrail.length > 0) && (
              <div className="px-4 py-2 border-b bg-slate-50 space-y-1.5">
                {viewerChain.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="font-semibold text-slate-500 uppercase tracking-wide mr-1">
                      Related
                    </span>
                    {viewerChain.map((c, i) => {
                      const current =
                        String(c.id) === String(viewer.row.record_id || viewer.row.id);
                      return (
                        <span key={c.id} className="inline-flex items-center gap-1">
                          {i > 0 ? <span className="text-slate-300">→</span> : null}
                          <button
                            type="button"
                            disabled={current}
                            onClick={() => openPreviewByRecordId(c.id)}
                            className={`rounded px-2 py-1 border ${
                              current
                                ? 'bg-primary-600 text-white border-primary-700 font-semibold'
                                : 'bg-white text-primary-800 border-slate-200 hover:bg-primary-50'
                            }`}
                            title={c.issued_date_display || ''}
                          >
                            {c.label}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                {viewerTrail.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="font-semibold text-slate-500 uppercase tracking-wide mr-1">
                      Back to
                    </span>
                    {viewerTrail.map((t) => (
                      <button
                        key={`trail-${t.id}`}
                        type="button"
                        onClick={() => openPreviewByRecordId(t.id, { pushTrail: false })}
                        className="rounded px-2 py-1 border bg-white text-slate-800 border-slate-200 hover:bg-slate-100"
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
            <div className="flex-1 overflow-auto bg-gray-100 min-h-[280px]">
              {viewer.kind === 'html' && viewer.html ? (
                <iframe
                  title="Paperwork preview"
                  srcDoc={viewer.html}
                  className="w-full h-[65vh] bg-white border-0"
                  sandbox="allow-same-origin"
                />
              ) : viewer.kind === 'html' && viewer.blobUrl ? (
                <iframe title="Paperwork preview" src={viewer.blobUrl} className="w-full h-[65vh] bg-white" />
              ) : viewer.blobUrl ? (
                <iframe title="Paperwork preview" src={viewer.blobUrl} className="w-full h-[65vh] bg-white" />
              ) : (
                <p className="p-6 text-sm text-gray-600">Preview not available — use Documents to download.</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 px-4 py-3 border-t bg-slate-50 items-center">
              {viewer.row?.mail_status ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-primary-700 hover:underline mr-1"
                  onClick={() =>
                    openMailLog(
                      viewer.row.document_id,
                      viewer.row.paperwork_label || viewer.row.document_name
                    )
                  }
                >
                  <DocumentMailStatus status={viewer.row.mail_status} />
                  {viewer.row.mail_to || 'Mail log'}
                </button>
              ) : null}
              {viewerStep === 'preview' ? (
                <>
                  <button
                    type="button"
                    onClick={() => setViewerStep('actions')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white font-semibold"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={closeViewer}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-white"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => openSend(viewer.row)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50 font-medium"
                  >
                    <Send className="w-4 h-4" /> Send
                  </button>
                  <button
                    type="button"
                    onClick={printViewer}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50 font-medium"
                  >
                    <Printer className="w-4 h-4" /> Print
                  </button>
                  {viewer.row.paperwork_type === 'DEFECT_NOTICE' || viewer.row.is_defect ? (
                    <button
                      type="button"
                      onClick={() => {
                        const path = reinspectionPath(viewer.row);
                        closeViewer();
                        navigate(path);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-red-300 bg-red-50 text-red-900 hover:bg-red-100 font-medium"
                    >
                      <Wrench className="w-4 h-4" /> Reinspection
                    </button>
                  ) : null}
                  {viewer.row.locked ? (
                    <button
                      type="button"
                      onClick={() => {
                        setAmendRow(viewer.row);
                        closeViewer();
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-amber-300 bg-amber-50 text-amber-950 font-medium"
                    >
                      <Pencil className="w-4 h-4" /> Amend
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={closeViewer}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-slate-800 text-white font-semibold"
                  >
                    <Eye className="w-4 h-4" /> OK
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewerStep('preview')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:underline ml-auto"
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {amendRow && (
        <PaperworkAmendModal
          buildingId={buildingId}
          row={amendRow}
          workers={workersList}
          onClose={() => setAmendRow(null)}
          onSuccess={() => {
            queryClient.invalidateQueries(['building', buildingId, 'paperwork']);
            queryClient.invalidateQueries(['docs-browse', `building-${buildingId}`]);
          }}
        />
      )}

      {sendTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md p-4 space-y-3">
            <h4 className="font-semibold">Send paperwork</h4>
            <p className="text-sm text-gray-600 truncate">
              {sendTarget.paperwork_label || sendTarget.document_name}
            </p>
            <label className="block text-sm">
              To (email)
              <input
                type="email"
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                placeholder="name@example.com"
              />
            </label>
            <label className="block text-sm">
              Subject
              <input
                type="text"
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Message
              <textarea
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                rows={4}
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={() => setSendTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-60"
                disabled={sendMutation.isPending || !sendTo.trim()}
                onClick={() =>
                  sendMutation.mutate({
                    docId: sendTarget.document_id,
                    to: sendTo.trim(),
                    subject: sendSubject.trim(),
                    message: sendMessage.trim(),
                  })
                }
              >
                {sendMutation.isPending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {extOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">Register external paperwork</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  For partner / Notified scans only. Issuer is stored as IQP.
                </p>
              </div>
              <button type="button" onClick={() => setExtOpen(false)} className="text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <label className="block text-sm">
              SS (not issued)
              <select
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                value={extSsCode}
                onChange={(e) => {
                  const code = e.target.value;
                  setExtSsCode(code);
                  const row = pendingRows.find((r) => r.ss_code === code);
                  if (row?.default_worker_id) {
                    setExtWorkerId(String(row.default_worker_id));
                  }
                }}
              >
                <option value="">Select SS…</option>
                {pendingRows.map((r) => (
                  <option key={r.ss_code} value={r.ss_code}>
                    {r.ss_code}
                    {r.ss_name && r.ss_name !== r.ss_code ? ` — ${r.ss_name}` : ''}
                    {String(r.job_status || '').toUpperCase() === 'NOTIFIED' ? ' (Notified)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Scanned file name / title
              <input
                type="text"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Form_12A_SS15.2.pdf"
                value={extName}
                onChange={(e) => onExtNameChange(e.target.value)}
              />
            </label>
            <p className="text-xs text-gray-600">Type (auto from name, or tap to set)</p>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'FORM_12A', label: 'Form 12A', cls: 'border-green-400 bg-green-50 text-green-900' },
                { id: 'S_RAD', label: 'S-RaD', cls: 'border-blue-400 bg-blue-50 text-blue-900' },
                { id: 'DEFECT_NOTICE', label: 'Defect', cls: 'border-red-400 bg-red-50 text-red-900' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setExtType(t.id)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${t.cls} ${
                    extType === t.id ? 'ring-2 ring-offset-1 ring-slate-400' : 'opacity-70'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <label className="block text-sm">
              IQP (issuer)
              <select
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                value={extWorkerId}
                onChange={(e) => setExtWorkerId(e.target.value)}
              >
                <option value="">Auto (default IQP / job assignee)</option>
                {workersList.map((w) => (
                  <option key={w.id} value={String(w.id)}>
                    {w.name || w.email || w.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Building document id (optional)
              <input
                type="text"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="From Documents cabinet"
                value={extDocId}
                onChange={(e) => setExtDocId(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-3 py-2 text-sm border rounded-lg"
                onClick={() => setExtOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={extBusy || !extType || !extSsCode}
                onClick={submitExternalRegister}
                className="px-3 py-2 text-sm rounded-lg bg-violet-700 text-white font-semibold disabled:opacity-50"
              >
                {extBusy ? 'Saving…' : 'Register'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mailLog && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold">Mail log</h4>
              <button type="button" onClick={() => setMailLog(null)} className="text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-800 truncate">{mailLog.label || mailLog.name}</p>
            <dl className="text-sm space-y-1.5">
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Status</dt>
                <dd className="font-medium text-gray-900 inline-flex items-center gap-1.5">
                  <DocumentMailStatus status={mailLog.status} />
                  {mailLog.status || '—'}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">To</dt>
                <dd className="text-gray-900 break-all">{mailLog.to || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Subject</dt>
                <dd className="text-gray-900">{mailLog.subject || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Sent</dt>
                <dd className="text-gray-900 tabular-nums">
                  {mailLog.sentAt ? String(mailLog.sentAt).replace('T', ' ').slice(0, 19) : '—'}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Opened</dt>
                <dd className="text-gray-900 tabular-nums">
                  {mailLog.openedAt ? String(mailLog.openedAt).replace('T', ' ').slice(0, 19) : '—'}
                </dd>
              </div>
            </dl>
            <div className="flex justify-end gap-2 pt-1">
              {mailLog.documentId ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-50"
                  onClick={() => {
                    const row = mailLog.rowForResend || {
                      document_id: mailLog.documentId,
                      paperwork_label: mailLog.label,
                      ss_code: mailLog.rowForResend?.ss_code,
                    };
                    setMailLog(null);
                    openSend(row, {
                      documentId: mailLog.documentId,
                      label: mailLog.label,
                      to: mailLog.to || '',
                      subject: mailLog.subject || undefined,
                    });
                  }}
                >
                  <Send className="w-3.5 h-3.5" />
                  {mailNeedsSend(mailLog.status) ? 'Send' : 'Resend'}
                </button>
              ) : null}
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded"
                onClick={() => setMailLog(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
